import React, { useState } from 'react';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { drillsAPI } from '../../services/api';
import { DRILL_CATEGORIES, Drill, SaveDrillRequest } from '../../types/drill';
import DiagramEditor from '../diagram/DiagramEditor';
import { parseDiagram } from '../diagram/types';
import DrillSheet from './DrillSheet';
import DrillThumbnail from './DrillThumbnail';
import AiButton from '../ai/AiButton';
import HelpHint from '../help/HelpHint';
import { useAiStatus, refreshAiStatus } from '../../hooks/useAiStatus';
import { aiAPI } from '../../services/api';
import { getErrorMessage } from '../../utils/errorMessage';

interface DrillFormModalProps {
  /** Szerkesztendő gyakorlat; null új gyakorlathoz. */
  drill: Drill | null;
  /** Új gyakorlatnál előtöltés (pl. egy edzésterv-blokkból). */
  initial?: SaveDrillRequest;
  onClose: () => void;
  onSaved: (drill: Drill) => void;
}

const toForm = (drill: Drill | null, initial?: SaveDrillRequest): SaveDrillRequest =>
  drill
    ? {
        title: drill.title,
        category: drill.category ?? '',
        description: drill.description ?? '',
        durationMinutes: drill.durationMinutes ?? null,
        players: drill.players ?? '',
        equipment: drill.equipment ?? '',
        ageGroupHint: drill.ageGroupHint ?? '',
        diagram: drill.diagram ?? null,
      }
    : { title: '', category: '', description: '', durationMinutes: null, players: '', equipment: '', ageGroupHint: '', diagram: null, ...initial };

const DrillFormModal: React.FC<DrillFormModalProps> = ({ drill, initial, onClose, onSaved }) => {
  const [form, setForm] = useState<SaveDrillRequest>(() => toForm(drill, initial));
  const [editingDiagram, setEditingDiagram] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ai = useAiStatus();
  const [drawing, setDrawing] = useState(false);

  const set = (patch: Partial<SaveDrillRequest>) => setForm((f) => ({ ...f, ...patch }));

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!form.title.trim()) {
      setError('Add meg a gyakorlat nevét.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload: SaveDrillRequest = {
        ...form,
        title: form.title.trim(),
        category: form.category || null,
        durationMinutes: form.durationMinutes || null,
      };
      const saved = drill ? await drillsAPI.update(drill.id, payload) : await drillsAPI.create(payload);
      onSaved(saved);
    } catch (err: any) {
      setError(getErrorMessage(err, 'A mentés nem sikerült.'));
    } finally {
      setSaving(false);
    }
  };

  // A rajz a leírásból, a létszámból és az eszközökből készül
  const drawWithAi = async () => {
    const description = [
      form.description,
      form.players ? `Létszám, szervezés: ${form.players}` : null,
      form.equipment ? `Eszközök: ${form.equipment}` : null,
    ]
      .filter(Boolean)
      .join('\n');
    setDrawing(true);
    setError(null);
    try {
      set({ diagram: await aiAPI.diagram(description, form.title || undefined) });
    } catch (err) {
      setError(getErrorMessage(err, 'Az AI most nem tudott rajzot készíteni.'));
    } finally {
      setDrawing(false);
      refreshAiStatus();
    }
  };

  const inputClass =
    'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

  return (
    <>
      <DrillSheet
        title={drill ? 'Gyakorlat szerkesztése' : 'Új gyakorlat'}
        onClose={onClose}
        footer={
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-md border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50">
              Mégse
            </button>
            <button
              type="button"
              onClick={() => handleSubmit()}
              disabled={saving}
              className="px-4 py-2 rounded-md bg-primary-600 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? 'Mentés...' : 'Mentés a gyakorlattárba'}
            </button>
          </div>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

          <div>
            <label htmlFor="drill-title" className={labelClass}>Név *</label>
            <input
              id="drill-title"
              type="text"
              maxLength={200}
              value={form.title}
              onChange={(e) => set({ title: e.target.value })}
              placeholder="pl. Rondó 4v2 a körben"
              className={inputClass}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label htmlFor="drill-category" className={labelClass}>Kategória</label>
              <select
                id="drill-category"
                value={form.category ?? ''}
                onChange={(e) => set({ category: e.target.value })}
                className={inputClass}
              >
                <option value="">Nincs</option>
                {DRILL_CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="drill-duration" className={labelClass}>Időtartam (perc)</label>
              <input
                id="drill-duration"
                type="number"
                min={0}
                max={600}
                value={form.durationMinutes ?? ''}
                onChange={(e) => set({ durationMinutes: e.target.value ? Number(e.target.value) : null })}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="drill-age" className={labelClass}>Ajánlott korosztály</label>
              <input
                id="drill-age"
                type="text"
                maxLength={50}
                value={form.ageGroupHint ?? ''}
                onChange={(e) => set({ ageGroupHint: e.target.value })}
                placeholder="pl. U11-U14"
                className={inputClass}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="drill-players" className={labelClass}>Létszám, szervezés</label>
              <input
                id="drill-players"
                type="text"
                maxLength={200}
                value={form.players ?? ''}
                onChange={(e) => set({ players: e.target.value })}
                placeholder="pl. 4v2, 6 fős csoportok"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="drill-equipment" className={labelClass}>Eszközök</label>
              <input
                id="drill-equipment"
                type="text"
                maxLength={300}
                value={form.equipment ?? ''}
                onChange={(e) => set({ equipment: e.target.value })}
                placeholder="pl. 8 bója, 2 kiskapu, labdák"
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label htmlFor="drill-description" className={labelClass}>Leírás</label>
            <textarea
              id="drill-description"
              rows={5}
              maxLength={4000}
              value={form.description ?? ''}
              onChange={(e) => set({ description: e.target.value })}
              placeholder="Menete, szabályok, coaching pontok, variációk..."
              className={inputClass}
            />
          </div>

          <div>
            <span className={labelClass}>Rajz</span>
            <button
              type="button"
              onClick={() => setEditingDiagram(true)}
              className="group block w-full max-w-md overflow-hidden rounded-md text-left ring-1 ring-gray-200 hover:ring-primary-400"
              aria-label={form.diagram ? 'Rajz szerkesztése' : 'Rajz készítése'}
            >
              <DrillThumbnail diagram={form.diagram} title={form.title || 'Gyakorlat'} />
            </button>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setEditingDiagram(true)}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium text-primary-600 hover:bg-primary-50"
              >
                <PencilSquareIcon className="h-4 w-4" />
                {form.diagram ? 'Rajz szerkesztése' : 'Rajz készítése'}
              </button>
              {ai.available && (
                <span className="inline-flex items-center gap-1">
                  <AiButton
                    variant="link"
                    onClick={drawWithAi}
                    loading={drawing}
                    loadingText="Rajzolok…"
                    disabled={(form.description?.trim().length ?? 0) < 20}
                    title={(form.description?.trim().length ?? 0) < 20 ? 'Előbb írd le a gyakorlatot a Leírás mezőben' : undefined}
                    className="px-2 py-1"
                  >
                    {form.diagram ? 'Újrarajzolás AI-val' : 'Rajz AI-val'}
                  </AiButton>
                  <HelpHint id="drills.aiDiagram" />
                </span>
              )}
              {form.diagram && (
                <button
                  type="button"
                  onClick={() => set({ diagram: null })}
                  className="rounded-md px-2 py-1 text-sm text-gray-500 hover:bg-gray-100 hover:text-red-600"
                >
                  Rajz törlése
                </button>
              )}
            </div>
          </div>
        </form>
      </DrillSheet>

      {editingDiagram && (
        <DiagramEditor
          initial={parseDiagram(form.diagram)}
          title={form.title || 'Gyakorlat'}
          onCancel={() => setEditingDiagram(false)}
          onSave={(diagram) => {
            set({ diagram: diagram ? JSON.stringify(diagram) : null });
            setEditingDiagram(false);
          }}
        />
      )}
    </>
  );
};

export default DrillFormModal;
